import get from 'lodash/get'

import { ganApi } from '@client/api'
import { fingerprint, matchesLocal } from './remoteVersion'
import storage, { cancelStorage } from './Storage'
import { addEvent } from './googleTagManager'
import { isStaffImpersonating } from './impersonation'
import { captureException } from './sentry'
import { NULL as BlockModelNULL } from '@client/components/Block/model'
import { BLOCK_TYPES } from '@client/components/Block/constants'
import { VIEW_TYPES } from '@client/components/constants'
import previewModel from '@client/components/Preview/model'
import { errorDialogModel } from '@client/components/ErrorDialog/model'
import { showRemoteChangeToast } from '@client/components/RemoteChangeToast/model'
import {
  ITEM_TYPE_MAIL,
  ITEM_TYPE_RESPONDER,
} from '@client/components/Editor/settings'

export const POLL_INTERVAL = 30000
// After a failed fetch, back off rather than hammering a server that is already
// unhappy. Resets on the next success.
export const MAX_BACKOFF_TICKS = 8

// Only these two are one row, one body, one editor step. Forms, landing pages
// and surveys share a row across four bodies and PUT a common design_settings
// blob from every step, so a naive content compare there would report changes
// that belong to a different wizard step. See FE-1354 / PRO-621.
const POLLED_KINDS = [ITEM_TYPE_MAIL, ITEM_TYPE_RESPONDER]

/**
 * Watches the server's copy of the open document and keeps an idle tab current.
 *
 * A tab with no unsaved changes swaps in the newer version silently. A tab with
 * unsaved changes is left alone and told, so the user can decide — this slice
 * warns, it does not block.
 */
export class DocumentSync {
  constructor() {
    this.intervalId = 0
    this.failures = 0
    this.skipTicks = 0
    this.inFlight = false
  }

  bind({ editorModel, appModel, history, fontloader }) {
    // A single intervalId slot, as in utils/Autosave.js — binding twice without
    // an unBind in between orphans the previous timer for the life of the page.
    this.unBind()

    this.editorModel = editorModel
    this.appModel = appModel
    this.history = history
    this.fontloader = fontloader

    this.intervalId = setInterval(this.tick, POLL_INTERVAL)
    document.addEventListener(
      'visibilitychange',
      this.onVisibilityChange,
      false
    )
  }

  unBind() {
    clearInterval(this.intervalId)
    this.intervalId = 0
    this.failures = 0
    // Or a rebind inherits the old backoff and silently skips its first ticks.
    this.skipTicks = 0
    this.inFlight = false
    document.removeEventListener(
      'visibilitychange',
      this.onVisibilityChange,
      false
    )
  }

  onVisibilityChange = () => {
    // Coming back to a tab that has been parked for ten minutes is exactly when
    // it is most likely to be stale, so don't make the user wait out the
    // interval before it corrects itself.
    if (document.visibilityState === 'visible') {
      this.tick()
    }
  }

  /**
   * Whether the document is one this slice polls at all. Everything else never
   * makes a request — this is how scope is enforced.
   */
  isInScope() {
    const editor = this.editorModel
    return !!editor && !!editor.id && POLLED_KINDS.indexOf(editor.kind) !== -1
  }

  /**
   * Cheap synchronous reads, all re-checked after the fetch resolves because
   * any of them can change during the await. A refusal is not an error: the
   * next tick tries again, so deferral needs no queue.
   */
  canSwap() {
    const editor = this.editorModel
    const app = this.appModel

    if (!editor.isSaved || editor.isSaving || !editor.isValid) {
      return false
    }
    // Both hold direct references into the block tree, which the swap replaces
    // wholesale with fresh models. Refusing keeps the user's open sidebar or
    // in-progress drag working; the reload lands on a later tick.
    if (
      get(app, 'editingSettings.type', BLOCK_TYPES.NULL) !== BLOCK_TYPES.NULL
    ) {
      return false
    }
    if (
      get(app, 'draggedComponent.type', BLOCK_TYPES.NULL) !== BLOCK_TYPES.NULL
    ) {
      return false
    }
    if (get(app, 'view') !== VIEW_TYPES.EDITOR) {
      return false
    }
    if (previewModel.showPreview || errorDialogModel.error.show) {
      return false
    }
    return true
  }

  tick = async () => {
    if (this.inFlight || !this.isInScope()) {
      return
    }
    if (document.visibilityState !== 'visible') {
      return
    }
    if (this.skipTicks > 0) {
      this.skipTicks -= 1
      return
    }

    // Nothing can come of a response we could neither swap in nor warn about,
    // so don't pay for one. canSwap() is still re-checked in apply(), because
    // any of its conditions can change while we await. A dirty tab always
    // fetches: warning is the useful outcome there.
    if (this.editorModel.isSaved && !this.canSwap()) {
      return
    }

    // Which document this request is for. The editor can be loaded with a
    // different one while we await, and applying the answer to whatever happens
    // to be open then would write one document's blocks into another.
    const target = { id: this.editorModel.id, kind: this.editorModel.kind }

    this.inFlight = true
    try {
      const item = await this.fetch()
      // The silent handlers resolve to undefined on any failure, so an absent
      // body covers 401, 404, 5xx and a dropped connection alike.
      if (!item || !item.body) {
        this.failures += 1
        this.skipTicks = Math.min(2 ** this.failures, MAX_BACKOFF_TICKS)
        return
      }
      this.failures = 0
      this.skipTicks = 0
      this.apply(item, target)
    } catch (e) {
      // Nothing above rethrows on its own, but apply() walks the whole block
      // tree. Without this a malformed document would reject on every tick,
      // unhandled and invisible, while the interval kept running.
      captureException(e)
      // When the swap itself threw, the baseline did not move, so the next tick
      // fetches the same body and throws again. Back off as for a failed fetch,
      // or every open tab reports the same error to Sentry every 30s.
      this.failures += 1
      this.skipTicks = Math.min(2 ** this.failures, MAX_BACKOFF_TICKS)
    } finally {
      this.inFlight = false
    }
  }

  fetch() {
    // The silent variants: the default handlers redirect the window on 401/404
    // and open a blocking modal on 5xx, which would throw the user out of the
    // editor on a background timer. See api/gan.js.
    const load = ganApi.getSilentFetchMethodByType(this.editorModel.kind)
    if (!load) {
      return Promise.resolve(null)
    }
    return load(this.editorModel.id)
  }

  apply(item, target) {
    const editor = this.editorModel

    // The editor moved to another document while the request was in flight.
    // Its own poll will pick this up; applying here would cross the two.
    if (editor.id !== target.id || editor.kind !== target.kind) {
      return
    }

    const remote = fingerprint(item)

    if (!remote || remote === editor.remoteFingerprint) {
      return
    }

    // The server has moved, but to content this tab already holds — so there is
    // nothing to adopt and nobody to warn about.
    //
    // This happens for real. Opening a document normalises it in memory before
    // anything is saved: Text blocks re-sync their `data-inline` (FE-1105), and
    // Image, Product and the buttons write settings on first measure or
    // translate their default label. So a tab drifts from the version it loaded
    // without the user touching anything, and roughly a minute later autosave
    // commits that drift. A second tab on the same document normalised it the
    // same way, so when it sees that save it must recognise its own content
    // rather than report a conflict — otherwise two idle tabs manufacture one,
    // and the count FE-1354 exists to produce is inflated by nobody's edits.
    //
    // Safe against masking a real edit: a real edit does not match what this
    // tab already has. Only the fields the server returned are compared, since
    // the autoresponder endpoint omits some that serialize() always has.
    if (matchesLocal(item, editor.serialize())) {
      editor.setRemoteFingerprint(remote)
      return
    }

    if (!this.canSwap()) {
      // Warn once per distinct remote version, or a dirty tab would raise the
      // same alarm every 30 seconds. Suppression is tracked separately from
      // `remoteFingerprint`, which must keep describing the version this
      // document actually reflects — advancing it here would leave the tab
      // unable to notice it was stale once it returned to clean.
      if (!editor.isSaved && remote !== editor.remoteChangeFingerprint) {
        editor.setRemoteChangePending(remote)
        this.count(true)
      }
      return
    }

    if (!this.adopt(item)) {
      return
    }

    this.count(false)
    showRemoteChangeToast()
  }

  /**
   * Replaces the open document with the server's copy, along with everything
   * that still points at the old one.
   *
   * Used by apply() for a clean tab, and by the save conflict dialog when the
   * user loads the newer version in place of their unsaved changes (FE-1441).
   *
   * @param {object} item - an item as returned by the API
   * @return {boolean} whether the swap happened
   */
  adopt(item) {
    const editor = this.editorModel

    // Nothing below this point runs unless the swap actually happened — a
    // refused reload must leave the document and its snapshots untouched.
    if (!editor.reloadFromServer(item)) {
      return false
    }

    // The snapshot belongs to the document we just discarded; the settings
    // sidebar's Cancel button would otherwise amend it back in.
    cancelStorage.clear()
    // Belt and braces: canSwap() already refuses while either of these points
    // into the block tree, but the tree they could point at is now gone.
    this.appModel.setEditingSettings(BlockModelNULL)
    this.appModel.setDraggedComponent(BlockModelNULL)

    this.fontloader.load(editor.getFonts())
    // After the swap, or an undo would amend the pre-reload document into the
    // new tree. The History autorun is delayed 1s, so the run our own mutations
    // scheduled deep-equals what clear() just wrote and overwrites it in place
    // rather than pushing a phantom entry.
    this.history.clear()
    storage.saveItem(editor.serialize())

    return true
  }

  /**
   * Counts a blocked save and how the user resolved it, so FE-1441's trial
   * shows how often a tab would have overwritten a newer version.
   *
   * @param {object} fields - the GTM event name plus any extra fields
   */
  countSaveConflict(fields) {
    if (isStaffImpersonating()) {
      return
    }
    addEvent({ ...fields, itemKind: this.editorModel.kind })
  }

  count(dirty) {
    // Same single guard the other analytics sinks go through in
    // components/App/index.jsx, so none can silently bypass it.
    if (isStaffImpersonating()) {
      return
    }
    addEvent({
      event: 'editorRemoteChange',
      dirty,
      itemKind: this.editorModel.kind,
    })
  }
}

export default new DocumentSync()
